import React from "react";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { StyledButton } from "../../../shared/components/buttons/custom-button";
import { ValidationTimelineStep } from "../types/audit-data.types";
import { ExtractedAuditData } from "../utils/excel-parser";
import { ExtractedMetadataSummary } from "./ExtractedMetadataSummary";

interface ValidationTimelineProps {
  timelineSteps: ValidationTimelineStep[];
  allTimelinePassed: boolean;
  extractedData: ExtractedAuditData | null;
  uploading: boolean;
  successMsg: string | null;
  onUpload: () => void;
  onClear: () => void;
  notify: (msg: string, type: "success" | "error" | "info" | "warning") => void;
}

export const ValidationTimeline: React.FC<ValidationTimelineProps> = ({
  timelineSteps,
  allTimelinePassed,
  extractedData,
  uploading,
  successMsg,
  onUpload,
  onClear,
  notify,
}) => {
  return (
    <Paper elevation={1} sx={{ mt: 3, p: 3, borderRadius: 3, backgroundColor: "#ffffff" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="700" color="text.primary">
          Validation Timeline &amp; Status
        </Typography>
        <Chip
          label={allTimelinePassed ? "Validation Passed" : "Validation Incomplete"}
          color={allTimelinePassed ? "success" : "error"}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {timelineSteps.map((step) => (
          <Paper
            key={step.id}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: step.passed ? "#e0e0e0" : "#ffcdd2",
              backgroundColor: step.passed ? "#fcfdfc" : "#fff8f8",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              {step.passed ? (
                <CheckCircleOutlineIcon color="success" sx={{ mt: 0.2 }} />
              ) : (
                <ErrorOutlineIcon color="error" sx={{ mt: 0.2 }} />
              )}
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="subtitle2" fontWeight="600">
                    Step {step.id}: {step.label}
                  </Typography>
                  <Chip
                    label={step.passed ? "Passed" : "Failed"}
                    color={step.passed ? "success" : "error"}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: "0.75rem", fontWeight: 700 }}
                  />
                </Box>
                {step.message && (
                  <Typography
                    variant="body2"
                    color={step.passed ? "text.secondary" : "error.main"}
                    sx={{ mt: 0.5 }}
                  >
                    {step.message}
                  </Typography>
                )}
                {step.details && step.details.length > 0 && !step.passed && (
                  <Box sx={{ mt: 1 }}>
                    {step.details.map((d, i) => {
                      const match = d.match(/^Expected Pattern:\s*(.+)$/i);
                      if (match) {
                        const patternText = match[1];
                        return (
                          <Box
                            key={i}
                            sx={{
                              backgroundColor: "rgba(240, 205, 127, 0.18)",
                              p: 2,
                              borderRadius: 3,
                              border: "1.5px solid rgba(201, 151, 56, 0.35)",
                              mt: 1.5,
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "#8c6210", display: "block", mb: 0.5 }}
                            >
                              Suggested File Name Format
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mb: 1.5, fontSize: "11px" }}
                            >
                              Format: <strong>ad_[cb-code]_[te-id]_[audit-dates]_[submission-date]</strong>
                            </Typography>

                            {/* Hero Value Container Box */}
                            <Box
                              sx={{
                                backgroundColor: "#FFFFFF",
                                border: "1px solid rgba(201, 151, 56, 0.25)",
                                borderRadius: "8px",
                                p: 1.2,
                                px: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.01)",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#2C4748",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flexGrow: 1,
                                  fontSize: "11.5px",
                                  fontFamily: "monospace",
                                }}
                              >
                                {patternText}
                              </Typography>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  navigator.clipboard.writeText(patternText);
                                  notify("Suggested file name copied!", "success");
                                }}
                                sx={{
                                  minWidth: 0,
                                  p: 0.5,
                                  textTransform: "none",
                                  color: "#8c6210",
                                  fontWeight: "bold",
                                  fontSize: "12px",
                                  textDecoration: "underline",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    textDecoration: "underline",
                                  },
                                }}
                              >
                                Copy
                              </Button>
                            </Box>
                          </Box>
                        );
                      }
                      return (
                        <Typography key={i} variant="caption" display="block" color="text.secondary">
                          • {d}
                        </Typography>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Extracted Data Summary Box */}
      {extractedData && allTimelinePassed && (
        <ExtractedMetadataSummary extractedData={extractedData} />
      )}

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "center" }}>
        <StyledButton
          variant="contained"
          disabled={!allTimelinePassed || uploading || !!successMsg}
          onClick={onUpload}
          startIcon={<CloudUploadOutlinedIcon />}
        >
          {uploading ? "Uploading…" : "Upload & Submit"}
        </StyledButton>
        <StyledButton
          variant="outlined"
          disabled={uploading}
          onClick={onClear}
          startIcon={<RestartAltIcon />}
        >
          Clear
        </StyledButton>
      </Box>
    </Paper>
  );
};
